import type {Component, Editor} from 'grapesjs';
import {getAllImagesInfo} from '@EmailMakerReact/lib/email-maker-media-library';
import {scrollToComponent} from '@EmailMakerReact/lib/email-maker-selection-library';
import {
  copyHtmlForMarketingEmail,
  copyMediaQueriesForMarketingEmail,
  copyTextForMarketingEmail,
  copyTextJson,
} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {isEmailMakerAutosaveEnabled, setEmailMakerAutosaveEnabled} from '@EmailMakerReact/lib/email-maker-storage-library';
import {saveUserComponentTemplate} from '@EmailMakerReact/lib/email-maker-section-template-library';

/**
 * Super-user-only editor operations backing the React email maker admin controls.
 * Implemented as focused functions over the GrapesJS editor model and the existing email-maker libs
 * (the admin actions are NOT GrapesJS commands — they are called directly from the admin component).
 */

const OPEN_TEMPLATE_TOOLS_ENDPOINT = 'emailmaker/openTemplateTools';
const DOWNLOAD_PREVIEW_ENDPOINT = 'emailmaker/downloadEmailTemplatePreview';
const NO_TEMPLATE_ID_MESSAGE = 'The email template has no ID. Has it been saved yet?';

/** GrapesJS marks a component's keep-out-of-selection flag under the `selectable` model prop. */
const SELECTABLE_PROP = 'selectable';

/** The preview sizes exposed by emailmaker/downloadEmailTemplatePreview. */
export enum EmailMakerAdminPreviewSize {
  DESKTOP = 'desktop',
  TABLET = 'tablet',
  PHONE = 'phone',
}

/** Logs the selected component's props for super-user inspection. */
export function logComponentProperties(component: Component): void {
  console.info(component.props());
}

/** Removes the selected component from the email. */
export function removeComponent(component: Component): void {
  component.remove();
}

/** Removes the selected component's parent (and everything inside it) from the email. */
export function removeParentComponent(component: Component): void {
  component.parent()?.remove();
}

/** Saves the selected component as a reusable section template; resolves once the save settles. */
export function saveComponentAsSectionTemplate(editor: Editor, component: Component): Promise<void> {
  return new Promise<void>((resolve) => {
    saveUserComponentTemplate(editor, {component, callback: resolve});
  });
}

/** Copies the marketing-formatted, inlined email HTML to the clipboard (shows the shared "Copied!" toast). */
export async function copyMarketingHtml(editor: Editor): Promise<void> {
  await copyHtmlForMarketingEmail(editor);
}

/** Copies the email's plain-text content to the clipboard (shows the shared "Copied!" toast). */
export async function copyMarketingText(editor: Editor): Promise<void> {
  await copyTextForMarketingEmail(editor);
}

/** Copies the email's CSS media queries to the clipboard (shows the shared "Copied!" toast). */
export async function copyMarketingMediaQueries(editor: Editor): Promise<void> {
  await copyMediaQueriesForMarketingEmail(editor);
}

/** Copies all text components as a JSON array to the clipboard (shows the shared "Copied!" toast). */
export async function copyTextComponentsJson(editor: Editor): Promise<void> {
  await copyTextJson(editor);
}

/** Opens the back-office template tools for the current template, or alerts when it has no id yet. */
export function openTemplateAdminTools(idEmailTemplate: string | null): void {
  if (!idEmailTemplate) {
    window.alert(NO_TEMPLATE_ID_MESSAGE);
    return;
  }
  window.open(window.PMW.util.site_url(`${OPEN_TEMPLATE_TOOLS_ENDPOINT}/${idEmailTemplate}`));
}

/** Opens the server-rendered preview download for the given device size in a new tab. */
export function openTemplatePreviewDownload(idEmailTemplate: string | null, size: EmailMakerAdminPreviewSize): void {
  if (!idEmailTemplate) {
    window.alert(NO_TEMPLATE_ID_MESSAGE);
    return;
  }
  window.open(window.PMW.util.site_url(`${DOWNLOAD_PREVIEW_ENDPOINT}/${idEmailTemplate}?size=${size}`), '_blank');
}

/** Serializes the project data onto the debug handle and logs it. */
export function logProjectJson(editor: Editor): void {
  window.grapesjsMeta ??= {};
  window.grapesjsMeta.pmwEmailJson = editor.getProjectData();
  console.info('saved to window.grapesjsMeta.pmwEmailJson:', window.grapesjsMeta.pmwEmailJson);
}

/** Exposes the editor on the debug handle for console access. */
export function exposeEditorOnWindow(editor: Editor): void {
  window.grapesjsMeta ??= {};
  window.grapesjsMeta.gjsEditor = editor;
  console.info('exposed editor under window as window.grapesjsMeta.gjsEditor');
}

/** True when GrapesJS autosave is currently live for this session. */
export function isAutosaveOn(editor: Editor): boolean {
  return isEmailMakerAutosaveEnabled(editor);
}

/** Flips autosave and returns the resulting state (which may differ from the request due to storage guards). */
export function toggleAutosave(editor: Editor): boolean {
  setEmailMakerAutosaveEnabled(editor, !isEmailMakerAutosaveEnabled(editor));
  return isEmailMakerAutosaveEnabled(editor);
}

/**
 * Restores each component's original `selectable` flag from a saved snapshot, keyed by GrapesJS cid.
 * Components absent from the snapshot are left untouched.
 */
export type AdminSelectionSnapshot = Record<string, boolean>;

/** Makes every component selectable and returns a snapshot of their prior `selectable` flags (keyed by cid). */
export function enableSelectionForAllComponents(editor: Editor): AdminSelectionSnapshot {
  const snapshot: AdminSelectionSnapshot = {};
  editor.getWrapper()?.onAll((component: Component) => {
    snapshot[component.cid] = Boolean(component.get(SELECTABLE_PROP));
    component.set(SELECTABLE_PROP, true);
  });
  return snapshot;
}

/** Restores the `selectable` flag of every component captured in the snapshot. */
export function restoreSelectionFromSnapshot(editor: Editor, snapshot: AdminSelectionSnapshot): void {
  editor.getWrapper()?.onAll((component: Component) => {
    const previous = snapshot[component.cid];
    if (previous !== undefined) {
      component.set(SELECTABLE_PROP, previous);
    }
  });
}

/** Replaces the editor's components with parsed custom HTML (super-user import). */
export function uploadCustomHtmlIntoEditor(editor: Editor, html: string): void {
  editor.setComponents(html);
}

/** One image/video/background component's resolved source, for the Check-image-sources grid. */
export interface EmailImageSourceInfo {
  componentId: string;
  url: string;
  source: string;
}

/**
 * Resolves the URL + source for every image/video/background component in the email, concurrently.
 * Reuses the shared media walk + asset lookup; components whose lookup fails are skipped (logged, not thrown).
 */
export async function getEmailImageSources(editor: Editor): Promise<EmailImageSourceInfo[]> {
  const images = await getAllImagesInfo(editor);
  return images.map((info) => ({componentId: info.component.getId(), url: info.url, source: info.source}));
}

/** Selects the component with the given id and smooth-scrolls the canvas to it; returns whether it was found. */
export function selectComponentById(editor: Editor, componentId: string): boolean {
  const matched = editor.getWrapper()?.find(`#${componentId}`) ?? [];
  const component = matched[0];
  if (!component) {
    return false;
  }
  editor.select(component);
  scrollToComponent(editor, {component});
  return true;
}

/** No-op placeholder for the "test" buttons. */
export function runAdminTestNoop(): void {
  return undefined;
}
